import React, { useEffect, useState, useMemo } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import { showErrorToast } from "../components/toasts/ToastService";
import { useOrderSave } from "../context/OrderSaveContext";
import { IconCheck } from "@tabler/icons-react";

const normalizeMountingTypeValue = (value) => {
    if (value === null || value === undefined) return 1;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue !== 0 ? numericValue : 1;
};

const RogzitesTipus = () => {
    const navigate = useStepNavigate();
    const { order, updateOrder } = useOrder();
    const [selectedOption, setSelectedOption] = useState(() =>
        normalizeMountingTypeValue(order.hmeliorder?.hmeliorder_mounting_type ?? 1)
    );
    const [selectedOptionExtra, setSelectedOptionExtra] = useState(
        order.hmeliorder?.hmeliorder_extra_zipper === 1 ? "Igen" : "Nem"
    );
    const [loading, setLoading] = useState(false);
    const { saveOrder } = useOrderSave();
    const mountingOptionsRaw = order.model_options?.mounting_types_for_model;
    const mountingOptions = useMemo(() => {
        if (Array.isArray(mountingOptionsRaw)) return mountingOptionsRaw;
        if (mountingOptionsRaw && typeof mountingOptionsRaw === "object")
            return Object.values(mountingOptionsRaw);
        return [];
    }, [mountingOptionsRaw]);

    const rogzitesOptions = mountingOptions
        .filter((opt) => Number(opt.mounting_type_szam) !== 5)
        .map((opt, idx) => {
            const numericValue = Number(opt.mounting_type_szam);
            const isNumeric = Number.isFinite(numericValue);
            return {
                key: `${opt.mounting_type_szam ?? idx}`,
                label: opt.mounting_type_name,
                value: isNumeric ? numericValue : null,
                disabled: Boolean(opt.disabled) || !isNumeric,
            };
        });

    const zipOption = mountingOptions.find((opt) => Number(opt.mounting_type_szam) === 5);
    const isZipDisabled = zipOption ? Boolean(zipOption.disabled) : true;
    const hasOptions = mountingOptions.length > 0;
    const hasSelection = Number.isFinite(selectedOption) && selectedOption > 1;
    const allOptionsDisabled = rogzitesOptions.length > 0 && rogzitesOptions.every((opt) => opt.disabled);
    const canSkipSelection = !hasOptions || allOptionsDisabled;

    const handleOptionSelected = (value) => {
        if (value === null) return;
        const nextValue = selectedOption === value ? 1 : value;
        setSelectedOption(nextValue);
        updateOrder({
            ...order,
            hmeliorder: {
                ...(order.hmeliorder || {}),
                hmeliorder_mounting_type: nextValue === 1 ? 1 : nextValue,
            },
        });
    };

    const handleOptionSelectedExtra = () => {
        if (isZipDisabled) return;
        const nextValue = selectedOptionExtra === "Igen" ? "Nem" : "Igen";
        setSelectedOptionExtra(nextValue);
        updateOrder({
            ...order,
            hmeliorder: {
                ...(order.hmeliorder || {}),
                hmeliorder_extra_zipper: nextValue === "Igen" ? 1 : 0,
            },
        });
    };

    const handleNext = async () => {
        if (!hasSelection && !canSkipSelection) {
            showErrorToast("Kérlek válassz rögzítési típust!");
            return;
        }

        setLoading(true);

        const updatedOrder = {
            ...order,
            hmeliorder: {
                ...(order.hmeliorder || {}),
                hmeliorder_mounting_type: hasSelection ? selectedOption : 1,
                hmeliorder_extra_zipper: selectedOptionExtra === "Igen" ? 1 : 0,
            },
        };

        try {
            updateOrder(updatedOrder);
            const result = await saveOrder(updatedOrder);

            if (result?.success !== false) {
                navigate("/SpecialisIgeny");
            } else {
                showErrorToast(result?.message || "Hiba történt a rendelés mentése során!");
            }
        } catch (err) {
            showErrorToast("Hiba történt a rendelés mentése során: " + (err?.message || err));
        } finally {
            setLoading(false);
        }
    };

    const canProceed = (hasSelection || canSkipSelection) && !loading;

    return (
        <TablerLayout title="Rögzítés típusa">
            <div className="row justify-content-center">
                <div className="col-md-10">
                    <div className="card-body">
                        <div className="mb-5">
                            {!hasOptions ? (
                                <div className="alert alert-warning text-center mb-0">
                                    Nem választható rögzítési típus.
                                </div>
                            ) : (
                                <div className="row g-4 justify-content-center">
                                    {rogzitesOptions.map((option) => (
                                        <div className="col-12 col-md-6" key={option.key}>
                                            <button
                                                type="button"
                                                className={`btn w-100 py-3 px-3 shadow-sm d-flex flex-column align-items-center fw-bold
                                                    ${selectedOption === option.value ? "btn-primary" : "btn-outline-primary bg-white text-primary border-primary"}`}
                                                style={{
                                                    fontSize: 20,
                                                    fontWeight: 600,
                                                    borderWidth: 2,
                                                    minHeight: 100,
                                                    cursor: option.disabled ? "not-allowed" : "pointer",
                                                    opacity: option.disabled ? 0.6 : 1,
                                                }}
                                                onClick={() => !option.disabled && handleOptionSelected(option.value)}
                                                disabled={option.disabled}
                                                title={option.disabled ? "Ez a rögzítési típus nem elérhető" : ""}
                                            >
                                                <span
                                                    className="fw-bold"
                                                    style={{ color: option.disabled ? "var(--primary-color)" : undefined }}
                                                >
                                                    {option.label}
                                                </span>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="mb-4">
                            <label className="form-label fw-bold fs-5 mb-3 d-block text-center">Kér cipzárt?</label>
                            <div className="d-flex justify-content-center">
                                <button
                                    type="button"
                                    className={`btn p-3 d-flex align-items-center shadow-sm
                                        ${selectedOptionExtra === "Igen" ? "btn-light border-primary" : "btn-outline-secondary"}`}
                                    style={{
                                        minWidth: 200,
                                        fontSize: 20,
                                            fontWeight: 600,
                                            minHeight: 100,
                                            minWidth: 350,
                                        borderWidth: selectedOptionExtra === "Igen" ? 2 : 1
                                    }}
                                    onClick={handleOptionSelectedExtra}
                                    disabled={isZipDisabled}
                                    title={isZipDisabled ? "A cipzár opció nem elérhető ennél a modellnél" : ""}
                                >
                                    <div
                                        className={`d-flex align-items-center justify-content-center me-3 rounded-3
                                            ${selectedOptionExtra === "Igen" ? "bg-primary" : "bg-light border border-secondary"}`}
                                        style={{ width: 32, height: 32 }}
                                    >
                                        {selectedOptionExtra === "Igen" && (
                                            <IconCheck color="white" size={22} strokeWidth={3} />
                                        )}
                                    </div>
                                    <span className="fw-bold text-primary">
                                        {selectedOptionExtra === "Igen" ? "Cipzár hozzáadva" : "Cipzár hozzáadása"}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="card-footer d-flex justify-content-between">
                        <StepperNavButton side="left" label="Vissza" onClick={() => navigate("/ModellValaszto")} />
                        <StepperNavButton
                            side="right"
                            label="Tovább"
                            onClick={handleNext}
                            disabled={!canProceed}
                        />
                    </div>
                </div>
            </div>
        </TablerLayout>
    );
};

export default RogzitesTipus;
